/**
 * Every hard-coded string in the Climb Plan PDF, mirroring
 * careerPlanPDFTexts.ts.
 *
 * ASCII ONLY. A character outside the embedded font subset renders as nothing
 * at all, with no warning and no error — so no curly quotes, no en dashes, no
 * arrows, and `|` rather than the middot the review uses between grade and ID.
 */
export const climbPlanPDFTexts = {
  header: {
    /** Spelled out in the top right, where the date used to sit. */
    studentIdLabel: "Student ID:",
    updatedLabel: "Last saved",
  },

  footer: {
    documentTitle: "Climb Plan",
    page: (current: number, total: number) => `Page ${current} of ${total}`,
  },

  identity: {
    /** "<Name>'s Climb Plan" - the document's only title. */
    title: (name: string) => `${name}'s Climb Plan`,
    myCore: "My Core Personality Types",
    myWorkValues: "My Work Values",
    signedBy: "Signed by",
  },

  /**
   * Page one: the hero, the section bands, and the cards inside them.
   *
   * The BAND headings are the uppercase ones that sit outside a card -
   * "MY PROFILE", "HIGH SCHOOL PRIORITIES". The CARD labels are the small
   * overlines inside one. A card's label is sentence case because it names a
   * field; a band's is uppercase because it names a part of the document.
   */
  summary: {
    topHat: "TOP HAT",
    highSchoolPriorities: "HIGH SCHOOL PRIORITIES",
    /** Under the band, over the two cards. */
    highSchoolPrioritiesIntro: (first: string) =>
      `${first} is interested in the following high school options.`,
    afterHighSchoolBand: "AFTER HIGH SCHOOL",
    /** Under the band, over the paths. */
    afterHighSchoolIntro: (first: string, hat: string | null) =>
      `${first} is interested in the following post-secondary options${hat ? ` for ${hat}` : ""}.`,
    whatsInside: "WHAT'S INSIDE THE CLIMB PLAN",

    highSchool: "High School",
    program: "Program",
    /** Between two After High School paths: they are alternatives. */
    or: "or",

    /**
     * The four parts of the document, as the What's Inside grid names them.
     * Semi-static: only the climber's first name, their program and their
     * Hat vary, and each has a generic line for when it is missing. Straight
     * apostrophes - the font subset has no curly one.
     */
    inside: {
      profile: (first: string) => ({
        name: `${first}'s Profile`,
        text: "Core personality and work values assessment results",
      }),
      topHatFit: (first: string) => ({
        name: `${first}'s Top Hat Fit`,
        text: `Explore connections between ${first}'s Profile and career interest`,
      }),
      highSchool: (first: string, program: string | null) => ({
        name: `${first}'s High School Priorities`,
        text: program
          ? `About the ${program} Program`
          : "About the high school program and school chosen",
      }),
      educationAndTraining: (hat: string | null) => ({
        name: "Education and Training",
        text: hat
          ? `Postsecondary options and skills related to ${hat}`
          : "Postsecondary options and skills related to the Top Hat",
      }),
    },
  },

  /**
   * The four MAJOR sections are headed in the climber's name, title case,
   * and printed large (SectionTitle's `major`); every other section keeps
   * the small uppercase caption. The major ones are the four parts page one's
   * What's Inside grid promises.
   */
  sections: {
    myProfile: (first: string) => `${first}'s Core Personality & Work Values`,
    myProfileSubtitle: "Self-assessment results and descriptions",
    /**
     * What the two measures ARE, for a reader meeting them for the first time.
     *
     * Authored here rather than read from the CMS facet types: only one of the
     * two carries a description there (Core Personality's is empty), so half
     * this box would be blank.
     */
    whatTheseAre: [
      {
        name: "Core Personality",
        text: "Career experts have identified six occupational personality types based on people's shared interests and work preferences. Understanding a person's mix of personality types is helpful for identifying which careers fit best.",
      },
      {
        name: "Work Values",
        text: "Career experts have identified a set of six primary work values based on what people need to feel satisfied at work. Knowing a person's work values is helpful for finding careers that will feel rewarding.",
      },
    ],
    /** Over the four Work Values the climber did not place. */
    otherWorkValues: "Other Work Values",

    topHat: (first: string) => `${first}'s Top Hat Fit`,
    topHatSubtitle: (first: string) =>
      `Explore connections between ${first}'s Profile and career interest`,
    corePersonalityTypes: "Core Personality Types",
    corePersonalitySubtitle: "What kind of person enjoys this work?",
    workValues: "Work Values",
    workValuesSubtitle: "What makes this work rewarding?",
    perksAndQuirks: "Perks & Quirks",
    personalityBadges: (first: string) =>
      `${first}'s Personality Badges that connect to this Top Hat`,
    /** Over the Connect Your Profile reflection, when there is one. */
    planningNotes: (first: string) => `${first}'s Planning Notes`,

    /** No subtitle: the program's own name and blurb follow directly. */
    highSchoolPriorities: (first: string) =>
      `${first}'s High School Priorities`,
    availableSchools: "Available at these High Schools",
    /** On the school the climber chose, in the list of all of them. */
    interested: "Interested",
    /** "<First> loved" beside another Hat, after its rating glyph. */
    otherHatRating: (first: string, label: string) =>
      `${first} ${label.toLowerCase()}`,
    availableCredentials: "Available Credentials",
    sampleCourses: "Sample Course Sequence",
    otherHats: "Other Hats relating to this Program",

    programSpotlight: "PROGRAM SPOTLIGHT",

    keysToSuccess: "KEYS TO SUCCESS",
    /** "an aspiring <Hat>" rather than a plural: hat names do not pluralise safely. */
    keysToSuccessSubtitle: (first: string, hat: string | null) =>
      `High school areas of focus${hat ? ` for an aspiring ${hat}` : ""}. ${first} chose one as a goal.`,

    ctsos: "CTSOS",
    ctsosSubtitle: (district: string | null) =>
      `Related Career and Technical Student Organizations recommended by ${district ?? "the district"}.`,

    afterHighSchool: "After High School",
    /** The section only prints when at least one path is being considered. */
    afterHighSchoolSubtitle: (first: string, hat: string | null) =>
      `Popular paths and other routes for ${hat ?? "this career"}. ${first} is considering at least one option.`,

    skillsToBuild: "SKILLS TO BUILD",
    /**
     * Two sentences: what the list is, then what the climber made of it. The
     * second is left off when they have picked nothing yet.
     */
    skillsToBuildSubtitle: (
      first: string,
      hat: string | null,
      strengthType: string | null,
      goalCount: number,
    ) => {
      const picks = [
        strengthType ? `one ${strengthType} as a strength` : null,
        goalCount === 1
          ? "one skill as a goal"
          : goalCount === 2
            ? "two skills as goals"
            : null,
      ].filter(Boolean);
      return [
        `The durable skills (PowerSkills) and technical skills (ProSkills) that are important${hat ? ` for an aspiring ${hat}` : ""}.`,
        picks.length > 0
          ? `${first} has identified ${picks.join(" and ")}.`
          : null,
      ]
        .filter(Boolean)
        .join(" ");
    },
    powerSkills: "PowerSkills",
    proSkills: "ProSkills",
    powerSkill: "PowerSkill",
    proSkill: "ProSkill",
    /** The chips beside a skill, as SkillCard's badges read. */
    strength: "Strength",
    goal: "Goal",

    shoutOuts: "SHOUT OUTS",
    shoutOutsSubtitle: (first: string) =>
      `Skill recognition that ${first} has received.`,
  },

  labels: {
    fit: "FIT",
    /** Over the Top Hat's fit message. */
    fitReport: "Fit Report",
    /** Over Connect Your Profile, naming which path the climber took. */
    coreConnected: "Core Connected",
    blocksConnected: (first: string) =>
      `${first}'s Building Blocks that connect to this Top Hat`,
    likes: "Likes:",
    dislikes: "Dislikes:",
    /** The chip on every After High School path, as the screen badges it. */
    considering: "Considering",
  },
} as const;
